<script setup lang="ts">
import { useJobBoardStore } from '@/core/collections/job-boards/job-board.store';

const props = defineProps({
   position: {
      type: Object,
      default: () => ({}),
   },
});

const jobBoardStore = useJobBoardStore();

const formattedDate = computed(() => {
   if (!props.position?.created_at) return '';
   const date = new Date(props.position.created_at);
   return new Intl.DateTimeFormat('nl-BE', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
   }).format(date);
});

const badges = computed(() => {
   const badgeList: string[] = [];

   // work schedules badges
   if (props.position?.profile?.profile_work_schedules?.length > 0) {
      props.position.profile.profile_work_schedules.forEach((schedule: any) => {
         if (schedule.work_schedule?.name) {
            badgeList.push(schedule.work_schedule.name);
         }
      });
   }

   // languages badges
   if (props.position?.profile?.profile_languages?.length > 0) {
      props.position.profile.profile_languages.forEach((lang: any) => {
         if (lang.language?.name) {
            badgeList.push(lang.language.name);
         }
      });
   }

   return badgeList;
});

const categoryImage = computed(() => {
   const jobFunctions = props.position?.profile?.profile_job_functions || [];

   // find first job function category with an image
   for (const jobFunc of jobFunctions) {
      const categoryId = jobFunc?.job_function?.job_function_category?.sync_id;
      if (!categoryId) continue;

      const category = jobBoardStore.categories.find((cat: any) => cat.sync_id === categoryId && cat.image_url);
      if (category?.image_url) {
         return category.image_url;
      }
   }

   return '';
});
</script>

<template>
   <header class="position-header">
      <img v-if="categoryImage" :src="categoryImage" class="position-header__image" />
      <div v-if="categoryImage" class="position-header__image-overlay"></div>
      <div class="position-header__content">
         <div>
            <h1>{{ position?.title }}</h1>
            <p>{{ position?.city?.name }}</p>
            <p>{{ $tb('public.position.online-since', { date: formattedDate }) }}</p>
            <div>
               <div v-for="(badge, index) in badges" :key="index" class="position-header__badge">
                  {{ badge }}
               </div>
            </div>
         </div>
         <div v-if="position?.wage_minimum != null || position?.wage_maximum != null">
            <h2>
               {{ position.wage_maximum != null ? `${position.wage_minimum} - ${position.wage_maximum}€` : `${position.wage_minimum}€` }}
            </h2>
            <p>{{ $tb('public.position.gross') }}</p>
         </div>
      </div>
   </header>
</template>

<style lang="scss" scoped>
.position-header {
   height: 300px;
   width: 100%;
   position: relative;
   background-color: var(--jobsite-primary);

   &__content {
      position: inherit;
      display: flex;
      justify-content: space-between;
      color: var(--nxt-white);
      height: 100%;
      align-items: center;
      max-width: 1000px;
      padding: 0 var(--nxt-gutter-xlarge);
      margin: 0 auto;
   }

   &__image {
      position: absolute;
      height: 100%;
      width: 100%;
      object-fit: cover;
   }

   &__image-overlay {
      position: absolute;
      height: 100%;
      width: 100%;
      background-color: rgba(0, 0, 0, 0.5);
   }

   &__badge {
      display: inline-block;
      background-color: var(--nxt-light-grey);
      color: var(--nxt-dark);
      padding: 4px 8px;
      border-radius: 4px;
      margin-right: 8px;
   }
}
</style>
